import { useState } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"

import { ApiTypes } from "types"
import { MonitoringReportAnnotations } from "types/MonitoringReportAnnotations"

import { AlertsAcknowledgeModal } from "modals/AlertsAcknowledgeModal"
import { AlertsFreezeModal } from "modals/AlertsFreezeModal"
import { AlertsUnfreezeModal } from "modals/AlertsUnfreezeModal"
import { StatsReportModal } from "modals/StatsReportModal"

const FreezeAcknowledgeButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-right: auto;
  gap: 4px;
`

const StatusButton = styled.span<{ disabled?: boolean; variant?: "acknowledge" | "active" | "frozen" }>`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 20px;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0.5;
  `}

  ${({ variant }) => {
    switch (variant) {
      case "acknowledge":
        return `
          border: 1px solid var(--text-link);
          background: var(--status-info-subtle);
          color: #4338ca;
        `
      case "active":
        return `
          border: 1px solid #2db2cb;
          background: var(--accent-subtle);
          color: #165964;
        `
      case "frozen":
        return `
          border: 1px solid #14b8a6;
          background: #dffbf4;
          color: #115e59;
        `
      default:
        return ""
    }
  }}
`

interface AlertsActionsProps {
  selectedAlerts: ApiTypes.Model.Alert[]
  onSuccess: () => void
  isVendor?: boolean
  selectedDevice?: ApiTypes.Model.Device | null
  monitoringReportAnnotations?: MonitoringReportAnnotations | null
  onClearMonitoringReportAnnotations?: () => void
  onCloseStatsModal?: () => void
}

export const AlertsActions = ({
  selectedAlerts,
  onSuccess,
  isVendor,
  selectedDevice,
  monitoringReportAnnotations,
  onClearMonitoringReportAnnotations,
  onCloseStatsModal,
}: AlertsActionsProps) => {
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false)
  const [showFreezeModal, setShowFreezeModal] = useState(false)
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false)

  const checkSelectedAlertsDiffer = () => {
    if (selectedAlerts.length === 0) return false
    const firstStatus = selectedAlerts[0].status
    const statusDiffer = selectedAlerts.some(alert => alert.status !== firstStatus)

    if (statusDiffer) {
      toast.error("Action cannot be applied. Please select alerts with the same status.")
      return true
    }

    return false
  }

  const onAcknowledgeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowAcknowledgeModal(true)
  }

  const onFreezeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowFreezeModal(true)
  }

  const onUnfreezeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowUnfreezeModal(true)
  }

  const handleSuccess = () => {
    onSuccess()
    setShowAcknowledgeModal(false)
    setShowFreezeModal(false)
    setShowUnfreezeModal(false)
  }

  const isFrozen = selectedAlerts.some(item => item.status === "frozen")
  const isDisabled = selectedAlerts.length === 0

  return (
    <>
      <FreezeAcknowledgeButtonsContainer>
        <StatusButton variant="acknowledge" disabled={isDisabled} onClick={onAcknowledgeClick}>
          Acknowledge
        </StatusButton>
        <StatusButton
          variant={isFrozen ? "frozen" : "active"}
          disabled={isDisabled}
          onClick={isFrozen ? onUnfreezeClick : onFreezeClick}
        >
          {isFrozen ? "Unfreeze" : "Freeze"}
        </StatusButton>
      </FreezeAcknowledgeButtonsContainer>
      {showAcknowledgeModal && (
        <AlertsAcknowledgeModal
          isVendor={isVendor}
          onCancel={() => setShowAcknowledgeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {showFreezeModal && (
        <AlertsFreezeModal
          isVendor={isVendor}
          onCancel={() => setShowFreezeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {showUnfreezeModal && (
        <AlertsUnfreezeModal
          isVendor={isVendor}
          onCancel={() => setShowUnfreezeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {selectedDevice && (
        <StatsReportModal
          onCancel={onCloseStatsModal || (() => {})}
          device={selectedDevice}
          monitoringReportAnnotations={monitoringReportAnnotations || null}
          onClearMonitoringReportAnnotations={onClearMonitoringReportAnnotations || (() => {})}
        />
      )}
    </>
  )
}
